/**
 * v135 — Share button.
 *
 * A standardized trigger that opens the ShareModal with a given snapshot.
 * Mount this anywhere — Launch view (build variant), Range session summary
 * (session variant), Compare view (compare variant). Pass a builder fn that
 * returns the snapshot lazily so we don't pay the cost of building it on
 * every render of the parent.
 */

import { useState, useCallback } from 'react';
import { Share2 } from 'lucide-react';
import { ShareModal } from './ShareModal';
import type { ShareSnapshot } from '../types';

interface ShareButtonProps {
  /** Lazy snapshot builder — only called when the user opens the modal. */
  getSnapshot: () => ShareSnapshot;
  label?: string;
  className?: string;
  /** Optional analytics hook. */
  onOpened?: () => void;
}

export function ShareButton({
  getSnapshot,
  label = 'Share',
  className,
  onOpened,
}: ShareButtonProps) {
  const [open, setOpen] = useState(false);
  const [snap, setSnap] = useState<ShareSnapshot | null>(null);

  const handleOpen = useCallback(() => {
    try {
      const s = getSnapshot();
      setSnap(s);
      setOpen(true);
      onOpened?.();
    } catch {
      // Snapshot construction failed (probably missing context) — quietly skip.
      // The parent can guard the button's visibility instead.
    }
  }, [getSnapshot, onOpened]);

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={
          className ??
          'inline-flex items-center gap-2 px-3 py-2 border border-cyan-500/50 text-cyan-300 rounded-md text-sm hover:bg-cyan-500/10'
        }
      >
        <Share2 className="w-4 h-4" />
        {label}
      </button>
      {snap && (
        <ShareModal
          open={open}
          snapshot={snap}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
